// src/services/rentalCollectionsService.js

// Real rental collections service using API endpoints
import { buildApiUrl } from '../config/environment';
import { getAuthToken, getCurrentUser } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// Helper function to get headers with auth token
const getHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper function to get agency ID
const getAgencyId = () => {
  // Get agency ID from session storage (where AuthContext stores it)
  const agencyId = sessionStorage.getItem('agencyId');
  return agencyId ? parseInt(agencyId) : null;
};

// Get all bank accounts for the agency
export const getBankAccounts = async () => {
  try {
    const agencyId = getAgencyId();
    if (!agencyId) throw new Error('No agency ID found');

    const response = await fetch(`${API_BASE_URL}/agencies/${agencyId}/bank-accounts`, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch bank accounts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching bank accounts:', error);
    throw error;
  }
};

// Get a specific bank account by ID
export const getBankAccount = async (accountId) => {
  try {
    const agencyId = getAgencyId();
    if (!agencyId) throw new Error('No agency ID found');

    const response = await fetch(`${API_BASE_URL}/agencies/${agencyId}/bank-accounts/${accountId}`, {
      method: 'GET',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch bank account: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching bank account:', error);
    throw error;
  }
};

// Create a new bank account
export const createBankAccount = async (accountData) => {
  try {
    const agencyId = getAgencyId();
    if (!agencyId) throw new Error('No agency ID found');

    const response = await fetch(`${API_BASE_URL}/agencies/${agencyId}/bank-accounts`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(accountData)
    });

    if (!response.ok) {
      throw new Error(`Failed to create bank account: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating bank account:', error);
    throw error;
  }
};

// Update a bank account
export const updateBankAccount = async (accountId, accountData) => {
  try {
    const agencyId = getAgencyId();
    if (!agencyId) throw new Error('No agency ID found');

    // Only send fields that the API actually accepts
    const updateData = {
      account_name: accountData.account_name,
      account_type: accountData.account_type
    };

    const response = await fetch(`${API_BASE_URL}/agencies/${agencyId}/bank-accounts/${accountId}`, {
      method: 'PATCH',
      headers: getHeaders(),
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      throw new Error(`Failed to update bank account: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating bank account:', error);
    throw error;
  }
};

// Delete a bank account
export const deleteBankAccount = async (accountId) => {
  try {
    const agencyId = getAgencyId();
    if (!agencyId) throw new Error('No agency ID found');

    const response = await fetch(`${API_BASE_URL}/agencies/${agencyId}/bank-accounts/${accountId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to delete bank account: ${response.statusText}`);
    }

    return response.ok;
  } catch (error) {
    console.error('Error deleting bank account:', error);
    throw error;
  }
};

// Get transactions for a bank account with cursor-based pagination
export const getBankAccountTransactions = async (accountId, params = {}) => {
  try {
    // No agencyId required for this endpoint
    // Build query parameters
    const queryParams = new URLSearchParams();

    // Pagination and filters (as per swagger)
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.cursor) queryParams.append('cursor', params.cursor);
    if (params.pagination_type) queryParams.append('pagination_type', params.pagination_type);
    if (params.forward !== undefined) queryParams.append('forward', params.forward);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_dir) queryParams.append('sort_dir', params.sort_dir);
    if (params.transaction_type) queryParams.append('transaction_type', params.transaction_type);
    if (params.min_amount) queryParams.append('min_amount', params.min_amount);
    if (params.max_amount) queryParams.append('max_amount', params.max_amount);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.description) queryParams.append('description', params.description);

    const response = await fetch(
      `${API_BASE_URL}/bank-accounts/${accountId}/transactions/search?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: getHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch transactions: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

// Upload transactions from CSV
export const uploadTransactions = async (accountId, csvFile) => {
  try {
    const formData = new FormData();
    formData.append('csv_file', csvFile);

    const token = getAuthToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/bank-accounts/${accountId}/transactions/batch`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to upload transactions: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading transactions:', error);
    throw error;
  }
};
